import { z } from "zod";

import { EventType } from "@classview/shared-types";

export enum EventVisibility {
  PRIVATE = "PRIVATE",
  ORGANISATION_WIDE = "ORGANISATION_WIDE",
  PUBLIC = "PUBLIC",
}

export const eventSchema = z
  .object({
    bannerS3Path: z.string().url({
      message: "Banner cannot be empty",
    }),
    category: z.string().min(1, "Category is required"),
    type: z.nativeEnum(EventType),
    isFree: z.boolean(),
    cost: z.number().min(1, "Cost must be at least 1").optional(),
    isApprovalRequired: z.boolean(),
    isCapacityCapped: z.boolean(),
    capacityLimit: z
      .number()
      .min(1, "Capacity limit must be at least 1")
      .optional(),
    // Session settings
    startDate: z.date({
      message: "Start date is required",
    }),
    startTime: z.date({
      message: "Start time is required",
    }),
    endTime: z.date({
      message: "End time is required",
    }),
    name: z
      .string({ message: "Name is required" })
      .min(1, "Title cannot be empty"),
    description: z
      .string({ message: "Description is required" })
      .min(1, "Description cannot be empty"),
    isClosedCaptionsEnabled: z.boolean(),
    isParticipantScreenSharing: z.boolean(),
    isRaisedHandSoundEnabled: z.boolean(),
    isChatEnabled: z.boolean(),
    isScheduledToRecord: z.boolean(),
    visibility: z.nativeEnum(EventVisibility, {
      errorMap: () => ({ message: "Visibility is required" }),
    }),
  })
  .refine(
    (data) => {
      // If not free, cost must be provided and >= 1
      if (!data.isFree && (!data.cost || data.cost < 1)) {
        return false;
      }
      return true;
    },
    {
      message: "Cost must be at least 1 when event is not free",
      path: ["cost"],
    },
  )
  .refine(
    (data) => {
      // If capacity is capped, capacityLimit must be provided and >= 1
      if (
        data.isCapacityCapped &&
        (!data.capacityLimit || data.capacityLimit < 1)
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Capacity limit must be at least 1 when capacity is capped",
      path: ["capacityLimit"],
    },
  );

export type EventSchema = z.infer<typeof eventSchema>;
